/**
 * Capacitor Native Bridge
 * 
 * Provides unified API for native features with web fallbacks.
 * When running in a native Capacitor shell, uses native plugins.
 * When running in browser, falls back to web APIs.
 */

import { Capacitor } from '@capacitor/core';

// Platform detection
export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // 'web' | 'ios' | 'android'

/**
 * Secure Storage — replaces localStorage for auth tokens
 */
export const secureStorage = {
  async get(key) {
    if (isNative) {
      const { Preferences } = await import('@capacitor/preferences');
      const { value } = await Preferences.get({ key });
      return value;
    }
    return localStorage.getItem(key);
  },

  async set(key, value) {
    if (isNative) {
      const { Preferences } = await import('@capacitor/preferences');
      await Preferences.set({ key, value });
    } else {
      localStorage.setItem(key, value);
    }
  },

  async remove(key) {
    if (isNative) {
      const { Preferences } = await import('@capacitor/preferences');
      await Preferences.remove({ key });
    } else {
      localStorage.removeItem(key);
    }
  },
};

/**
 * Camera — native camera or web file picker
 */
export const nativeCamera = {
  async takePhoto() {
    if (isNative) {
      const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
      const photo = await Camera.getPhoto({
        quality: 80,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Prompt, // Let user choose camera or gallery
      });
      return photo.dataUrl;
    }
    // Web fallback: use file input
    return null;
  },

  async pickFromGallery() {
    if (isNative) {
      const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
      const photo = await Camera.getPhoto({
        quality: 80,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Photos,
      });
      return photo.dataUrl;
    }
    return null;
  },
};

/**
 * Native Share Sheet
 */
export const nativeShare = {
  async share({ title, text, url }) {
    if (isNative) {
      const { Share } = await import('@capacitor/share');
      await Share.share({ title, text, url });
      return true;
    }
    // Web fallback: navigator.share
    if (navigator.share) {
      await navigator.share({ title, text, url });
      return true;
    }
    return false;
  },
};

/**
 * Status Bar configuration
 */
export const statusBar = {
  async setDark() {
    if (isNative) {
      const { StatusBar, Style } = await import('@capacitor/status-bar');
      await StatusBar.setStyle({ style: Style.Dark });
      await StatusBar.setBackgroundColor({ color: '#0B3C5D' });
    }
  },

  async setLight() {
    if (isNative) {
      const { StatusBar, Style } = await import('@capacitor/status-bar');
      await StatusBar.setStyle({ style: Style.Light });
      await StatusBar.setBackgroundColor({ color: '#FFFFFF' });
    }
  },
};

/**
 * Haptic Feedback
 */
export const haptics = {
  async impact() {
    if (isNative) {
      const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
      await Haptics.impact({ style: ImpactStyle.Light });
    }
  },

  async notification() {
    if (isNative) {
      const { Haptics, NotificationType } = await import('@capacitor/haptics');
      await Haptics.notification({ type: NotificationType.Success });
    }
  },
};

/**
 * In-App Browser — for OAuth flows on native
 */
export const inAppBrowser = {
  async open(url) {
    if (isNative) {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url, presentationStyle: 'popover' });
      return true;
    }
    window.location.href = url;
    return false;
  },
};

/**
 * App lifecycle events
 */
export const appLifecycle = {
  async onResume(callback) {
    if (isNative) {
      const { App } = await import('@capacitor/app');
      App.addListener('resume', callback);
    }
  },

  async onDeepLink(callback) {
    if (isNative) {
      const { App } = await import('@capacitor/app');
      App.addListener('appUrlOpen', (data) => {
        callback(data.url);
      });
    }
  },
};

/**
 * Splash Screen
 */
export const splashScreen = {
  async hide() {
    if (isNative) {
      const { SplashScreen } = await import('@capacitor/splash-screen');
      await SplashScreen.hide();
    }
  },
};
